// src/app/job-details.jsx
import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import API_BASE_URL from '../apiConfig.js';

// token helper
const getToken = () => {
  try { return localStorage.getItem('userToken'); } catch { return null; }
};

// helper to ensure HH:mm:ss for API
const withSeconds = (hhmm = '') => (hhmm && hhmm.length === 5 ? `${hhmm}:00` : hhmm || '');

// Helper to convert UTC date/time to local date/time for editing
const utcToLocal = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return { localDate: '', localTime: '' };
  
  // Create UTC date from the stored values
  const utcDate = new Date(`${dateStr}T${timeStr}Z`);
  
  // Convert to local date and time strings for the inputs
  const localDate = utcDate.getFullYear() + '-' + 
    String(utcDate.getMonth() + 1).padStart(2, '0') + '-' + 
    String(utcDate.getDate()).padStart(2, '0');
  
  const localTime = String(utcDate.getHours()).padStart(2, '0') + ':' + 
    String(utcDate.getMinutes()).padStart(2, '0');
  
  return { localDate, localTime };
};

// Helper to convert local date/time back to UTC for saving
const localToUtc = (localDate, localTime) => {
  if (!localDate || !localTime) return { utcDate: '', utcTime: '' };
  
  // Create local date from the input values
  const localDateTime = new Date(`${localDate}T${localTime}`);
  
  // Convert to UTC date and time strings for the API
  const utcDate = localDateTime.getUTCFullYear() + '-' + 
    String(localDateTime.getUTCMonth() + 1).padStart(2, '0') + '-' + 
    String(localDateTime.getUTCDate()).padStart(2, '0');
  
  const utcTime = String(localDateTime.getUTCHours()).padStart(2, '0') + ':' + 
    String(localDateTime.getUTCMinutes()).padStart(2, '0') + ':00';
  
  return { utcDate, utcTime };
};

export default function JobDetails() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');

  const [job, setJob] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedJob, setEditedJob] = useState(null);
  const [saving, setSaving] = useState(false);
  const [userPermissions, setUserPermissions] = useState(null);
  const [users, setUsers] = useState([]);
  const [assignedUserIds, setAssignedUserIds] = useState(['all']);
  const [subscription, setSubscription] = useState('');
  const [isUserPickerVisible, setUserPickerVisible] = useState(false);

  // Load user permissions on component mount and check access
  useEffect(() => {
    const loadUserPermissions = async () => {
      try {
        const permissions = localStorage.getItem('userPermissions');
        if (permissions) {
          const parsedPermissions = JSON.parse(permissions);
          setUserPermissions(parsedPermissions);
          
          // Check if user has job permissions
          if (parsedPermissions?.job_perm === 'none') {
            alert("You don't have permission to view jobs. Please contact your account administrator.");
            navigate('/'); // Redirect to home page
            return;
          }
        }
      } catch (error) {
        console.error('Error loading user permissions:', error);
      }
    };
    loadUserPermissions();
  }, [navigate]);

  // Check if user has job write permissions
  const hasJobWritePermission = () => {
    return userPermissions?.job_perm === 'write';
  };

  // Check if user can see prices
  const canSeePrices = () => {
    return userPermissions?.price_perm === 'visible';
  };

  // Check if current user is assigned to this job
  const isJobAssignedToCurrentUser = () => {
    if (!job || !userPermissions) return false;
    
    const currentUserId = userPermissions.userId;
    
    // If job is assigned to 'all', current user is assigned
    if (job.assigned_type === 'all') return true;
    
    // Check if current user's ID is in assignedUserIds
    if (job.assignedUserIds && job.assignedUserIds.includes(currentUserId)) return true;
    
    return false;
  };

  // Check if user can edit this specific job
  const canEditThisJob = () => {
    // Owners and admins can ALWAYS edit any job (check this FIRST)
    if (userPermissions?.user_type === 'owner' || userPermissions?.user_type === 'admin') {
      return true;
    }
    
    // For other users, check if they have write permission
    if (!hasJobWritePermission()) return false;
    
    // If job is assigned to current user, they can edit
    if (isJobAssignedToCurrentUser()) return true;
    
    // If job is NOT assigned to them, check edit_all_jobs permission
    return userPermissions?.edit_all_jobs === 1;
  };

  // Check if user can delete this specific job
  const canDeleteThisJob = () => {
    // Owners and admins can ALWAYS delete any job (check this FIRST)
    if (userPermissions?.user_type === 'owner' || userPermissions?.user_type === 'admin') {
      return true;
    }
    
    // For other users, check if user has write permission AND (is assigned OR has delete_all_jobs)
    if (hasJobWritePermission()) {
      // If job is assigned to current user, they can delete
      if (isJobAssignedToCurrentUser()) return true;
      
      // If job is NOT assigned to them, check delete_all_jobs permission
      if (userPermissions?.delete_all_jobs === 1) return true;
    }
    
    // Users with only read permission can still delete if they have delete_all_jobs permission
    if (userPermissions?.job_perm === 'read' && userPermissions?.delete_all_jobs === 1) {
      return true;
    }
    
    return false;
  };

  const loadJob = async () => {
    if (!id) return;
    try {
      const token = getToken();
      const { data } = await axios.get(`${API_BASE_URL}/api/jobs/${encodeURIComponent(id)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      const normalized = {
        id: data.id,
        visitType: data.visitType, // 'job' | 'quote'
        customerId: data.customerId,
        customerName: data.customerName || 'Unknown Customer',
        title: data.title || '',
        description: data.description || '',
        price: data.price ?? '',
        selectedDate: data.selectedDate || '', // YYYY-MM-DD
        selectedTime: data.selectedTime || '', // HH:mm:ss
        notes: data.notes || '',
        assigned_type: data.assigned_type || 'all',
        assignedUserIds: data.assignedUserIds || [],
      };
      // keep price as string for TextInput
      normalized.price = String(normalized.price ?? '');
      
      // Set assignedUserIds based on assigned_type
      if (normalized.assigned_type === 'all') {
        setAssignedUserIds(['all']);
      } else if (normalized.assignedUserIds && normalized.assignedUserIds.length > 0) {
        setAssignedUserIds(normalized.assignedUserIds.map(id => id.toString()));
      } else {
        setAssignedUserIds(['all']);
      }
      
      setJob(normalized);
      setEditedJob(normalized);
    } catch (err) {
      console.error('Error fetching job:', err);
      alert('Failed to load job. Please try again later.');
    }
  };

  // Load users if subscription is free or pro (like new-job.jsx)
  useEffect(() => {
    const loadUsersAndSubscription = async () => {
      try {
        const token = getToken();
        const sub = localStorage.getItem('subscription');
        setSubscription(sub);

        // Only fetch users if subscription is 'free' or 'pro'
        if (sub === 'free' || sub === 'pro') {
          const response = await axios.get(`${API_BASE_URL}/api/getAllUsers`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setUsers(response.data || []);
        }
      } catch (err) {
        console.error('Error fetching users:', err);
      }
    };

    loadUsersAndSubscription();
  }, []);

  useEffect(() => {
    loadJob();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const toggleUserSelection = (userId) => {
    if (userId === 'all') {
      // If clicking 'All', select only 'all'
      setAssignedUserIds(['all']);
    } else {
      // If clicking a specific user, remove 'all' from selection
      const currentIds = assignedUserIds.filter(id => id !== 'all');
      
      if (currentIds.includes(userId)) {
        const newIds = currentIds.filter((id) => id !== userId);
        // If no users left, default back to 'all'
        setAssignedUserIds(newIds.length > 0 ? newIds : ['all']);
      } else {
        const newIds = [...currentIds, userId];
        // Check if all users are now selected
        if (newIds.length === users.length) {
          // All users selected, switch to 'all'
          setAssignedUserIds(['all']);
        } else {
          setAssignedUserIds(newIds);
        }
      }
    }
  };

  const saveEdits = async (extra = {}) => {
    if (!job || !editedJob) return;

    // Simple, straightforward title validation - check FIRST
    const titleValue = editedJob.title;
    if (titleValue === null || titleValue === undefined || titleValue === '' || titleValue.trim() === '') {
      alert('Job title is required. Please enter a title.');
      return;
    }

    // Check if anything actually changed
    const { localDate, localTime } = utcToLocal(job.selectedDate, job.selectedTime);
    const jobLocalTime = localTime ? `${localTime}:00` : job.selectedTime;
    
    const hasChanges = 
      editedJob.title !== job.title ||
      editedJob.description !== job.description ||
      editedJob.price !== job.price ||
      editedJob.selectedDate !== localDate ||
      editedJob.selectedTime !== jobLocalTime ||
      editedJob.notes !== job.notes ||
      JSON.stringify(assignedUserIds) !== JSON.stringify(
        job.assigned_type === 'all' ? ['all'] : (job.assignedUserIds || []).map(id => id.toString())
      ) ||
      Object.keys(extra).length > 0;

    // If nothing changed, just exit edit mode like cancel button
    if (!hasChanges) {
      setIsEditing(false);
      return;
    }

    // Convert local date/time back to UTC for saving
    const { utcDate, utcTime } = localToUtc(editedJob.selectedDate, editedJob.selectedTime?.slice(0, 5));

    // Determine assigned_type and assignedUserIds based on selection
    let assigned_type = null;
    let userIdsToSend = [];

    if (assignedUserIds.includes('all')) {
      assigned_type = 'all';
      userIdsToSend = [];
    } else {
      assigned_type = 'specific';
      userIdsToSend = assignedUserIds.map((id) => parseInt(id));
    }

    // Build payload - send empty strings for blank fields, 0 for blank price
    const payload = {
      customerName: job.customerName,
      title: editedJob.title.trim(),
      description: editedJob.description || '',
      price: editedJob.price && String(editedJob.price).trim() !== '' ? parseFloat(editedJob.price) : 0,
      selectedDate: utcDate || editedJob.selectedDate || '',
      selectedTime: utcTime || withSeconds((editedJob.selectedTime || '').slice(0, 5)) || '',
      notes: editedJob.notes || '',
      assigned_type: assigned_type,
      assignedUserIds: userIdsToSend,
      ...extra,
    };

    try {
      setSaving(true);
      const token = getToken();
      await axios.put(`${API_BASE_URL}/api/jobs/edit/${encodeURIComponent(job.id)}`, payload, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      setJob((prev) => ({ ...prev, ...payload, visitType: payload.visitType ?? prev.visitType }));
      setIsEditing(false);
      await loadJob(); // Reload to get updated data
    } catch (err) {
      console.error('Error saving job edits:', err);
      alert('Failed to save job edits. Please try again later.');
    } finally {
      setSaving(false);
    }
  };

  const deleteJob = async () => {
    if (!job) return;
    const ok = window.confirm('Are you sure you want to delete this job?');
    if (!ok) return;

    try {
      const token = getToken();
      await axios.delete(`${API_BASE_URL}/api/jobs/${encodeURIComponent(job.id)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      navigate('/jobs');
    } catch (err) {
      console.error('Error deleting job:', err);
      alert('Failed to delete job. Please try again later.');
    }
  };

  const prettyLocal = (dateStr, timeStr) => {
    if (!dateStr || !timeStr) return '';
    const d = new Date(`${dateStr}T${timeStr}Z`);
    return format(d, 'MMMM dd, yyyy hh:mm a');
  };

  // Helper to format local date/time for edit mode preview (no UTC conversion needed)
  const prettyLocalEdit = (dateStr, timeStr) => {
    if (!dateStr || !timeStr) return '';
    const d = new Date(`${dateStr}T${timeStr}`);
    return format(d, 'MMMM dd, yyyy hh:mm a');
  };

  if (!id) {
    return (
      <View style={[styles.container, { justifyContent: 'center', alignItems: 'center' }]}>
        <Text>No job id provided.</Text>
      </View>
    );
  }

  if (!job || !editedJob) {
    return (
      <View style={[styles.container, { justifyContent: 'center', alignItems: 'center' }]}>
        <Text>Loading…</Text>
      </View>
    );
  }

  return (
    <KeyboardAvoidingView
      style={{ minHeight: '100vh', backgroundColor: '#f0f8ff' }}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      <ScrollView contentContainerStyle={styles.container} keyboardShouldPersistTaps="handled">
        <Text style={[styles.header, { fontSize: 14, color: '#007AFF', textDecorationLine: 'underline', textAlign: 'right' }]}>
          {job.visitType === 'job' ? `Job #${job.id}` : `Quote #${job.id}`}
        </Text>

        {/* Customer */}
        <Text style={[styles.label, { marginTop: 0 }]}>Customer</Text>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
          <Text style={styles.value}>{job.customerName}</Text>
          <TouchableOpacity onPress={() => navigate(`/customer-details?id=${job.customerId}`)}>
            <Text style={styles.customerDetailsLink}>Customer Details</Text>
          </TouchableOpacity>
        </View>

        {/* Title */}
        <Text style={styles.label}>Job Title</Text>
        {isEditing ? (
          <>
            <TextInput
              style={styles.input}
              value={editedJob.title}
              onChangeText={(t) => setEditedJob((p) => ({ ...p, title: t }))}
              placeholder="Enter job title"
            />
            {(!editedJob.title || editedJob.title.trim() === '') && (
              <Text style={{ color: 'red', fontSize: 14, marginTop: 4 }}>
                Title is required.
              </Text>
            )}
          </>
        ) : (
          <Text style={styles.value}>{job.title}</Text>
        )}

        {/* Description */}
        <Text style={styles.label}>Description</Text>
        {isEditing ? (
          <TextInput
            style={[styles.input, { height: 80 }]}
            multiline
            value={editedJob.description}
            onChangeText={(t) => setEditedJob((p) => ({ ...p, description: t }))}
          />
        ) : (
          <Text style={styles.value}>{job.description || 'No Description'}</Text>
        )}

        {/* Price (only for visitType=job) */}
        {job.visitType === 'job' && canSeePrices() && (
          <>
            <Text style={styles.label}>Price</Text>
            {isEditing ? (
              <TextInput
                style={styles.input}
                keyboardType="numeric"
                value={editedJob.price}
                onChangeText={(t) => {
                  // Only allow numbers and decimal point
                  const numericValue = t.replace(/[^0-9.]/g, '');
                  // Prevent multiple decimal points
                  const parts = numericValue.split('.');
                  const formatted = parts.length > 2 
                    ? parts[0] + '.' + parts.slice(1).join('') 
                    : numericValue;
                  setEditedJob((p) => ({ ...p, price: formatted }));
                }}
                placeholder="0.00"
              />
            ) : (
              <Text style={styles.value}>
                <Text style={{ fontWeight: 'bold' }}>$</Text>
                {Number(job.price || 0).toLocaleString()}
              </Text>
            )}
          </>
        )}

        {/* Date / Time */}
        <Text style={styles.label}>Date</Text>
        {isEditing ? (
          <View style={{ gap: 8 }}>
            <View style={{ flexDirection: 'row', gap: 8, marginTop: 5 }}>
              <input
                type="date"
                value={editedJob.selectedDate || ''}
                onChange={(e) => setEditedJob((p) => ({ ...p, selectedDate: e.target.value }))}
                style={htmlInputStyle}
              />
              <input
                type="time"
                value={(editedJob.selectedTime || '').slice(0, 5)}
                onChange={(e) => {
                  const v = e.target.value; // HH:mm
                  setEditedJob((p) => ({ ...p, selectedTime: withSeconds(v) }));
                }}
                style={htmlInputStyle}
              />
            </View>
            <View style={{ marginTop: 6 }}>
              <Text style={{ color: '#007AFF', fontWeight: 'bold' }}>
                {prettyLocalEdit(editedJob.selectedDate, editedJob.selectedTime) || 'Select a valid date and time'}
              </Text>
            </View>
          </View>
        ) : (
          <Text style={styles.value}>{prettyLocal(job.selectedDate, job.selectedTime)}</Text>
        )}

        {/* Notes */}
        <Text style={styles.label}>Notes</Text>
        {isEditing ? (
          <TextInput
            style={[styles.input, { height: 80 }]}
            multiline
            value={editedJob.notes}
            onChangeText={(t) => setEditedJob((p) => ({ ...p, notes: t }))}
          />
        ) : (
          <Text style={styles.value}>{job.notes || 'No notes available'}</Text>
        )}

        {/* Assigned Users - only show if subscription is free or pro */}
        {(subscription === 'free' || subscription === 'pro') && (
          <>
            <Text style={styles.label}>Assigned to:</Text>
            {isEditing && userPermissions?.user_type !== 'secondary' ? (
              <>
                <TouchableOpacity 
                  onPress={() => setUserPickerVisible(true)} 
                  style={styles.assignedUsersContainer}
                >
                  <Text style={styles.assignedUsersText}>
                    {assignedUserIds.includes('all')
                      ? 'All'
                      : users
                          .filter((u) => assignedUserIds.includes(u.id.toString()))
                          .map((u) => `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email)
                          .join(', ')}
                  </Text>
                </TouchableOpacity>
                
                {isUserPickerVisible && (
                  <View style={styles.overlay}>
                    <View style={styles.userPickerCard}>
                      <View style={styles.userPickerHeader}>
                        <Text style={styles.modalTitle}>Select Users</Text>
                        <TouchableOpacity onPress={() => setUserPickerVisible(false)} style={styles.closeButton}>
                          <Text style={styles.closeButtonText}>✕</Text>
                        </TouchableOpacity>
                      </View>
                      <ScrollView style={styles.userListScroll}>
                        {/* All option */}
                        <TouchableOpacity
                          style={styles.userOption}
                          onPress={() => toggleUserSelection('all')}
                        >
                          <Text style={styles.userOptionText}>All</Text>
                          {assignedUserIds.includes('all') && (
                            <Text style={styles.checkMark}>✓</Text>
                          )}
                        </TouchableOpacity>
                        
                        {/* Individual users */}
                        {users.map((user) => {
                          const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email;
                          const isSelected = assignedUserIds.includes(user.id.toString());
                          return (
                            <TouchableOpacity
                              key={user.id}
                              style={styles.userOption}
                              onPress={() => toggleUserSelection(user.id.toString())}
                            >
                              <Text style={styles.userOptionText}>{fullName}</Text>
                              {isSelected && (
                                <Text style={styles.checkMark}>✓</Text>
                              )}
                            </TouchableOpacity>
                          );
                        })}
                      </ScrollView>
                      <TouchableOpacity
                        style={styles.modalButton}
                        onPress={() => setUserPickerVisible(false)}
                      >
                        <Text style={styles.modalButtonText}>Done</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                )}
              </>
            ) : (
              <Text style={styles.value}>
                {assignedUserIds.includes('all')
                  ? 'All'
                  : users
                      .filter((u) => assignedUserIds.includes(u.id.toString()))
                      .map((u) => `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email)
                      .join(', ')}
              </Text>
            )}
          </>
        )}

        {/* Primary action buttons */}
        {(canEditThisJob() || canDeleteThisJob()) && (
          <View style={styles.buttonRow}>
            {isEditing ? (
              <>
                <TouchableOpacity
                  style={[styles.saveButton, saving && { opacity: 0.7 }]}
                  onPress={() => saveEdits()}
                  disabled={saving}
                >
                  <Text style={styles.saveButtonText}>{saving ? 'Saving…' : 'Save'}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.cancelButton}
                  onPress={() => {
                    setEditedJob({ ...job });
                    setIsEditing(false);
                  }}
                >
                  <Text style={styles.cancelButtonText}>Cancel</Text>
                </TouchableOpacity>
              </>
            ) : (
              <>
                {canEditThisJob() && (
                  <TouchableOpacity
                    style={styles.editButton}
                    onPress={() => {
                      // Convert UTC date/time to local time for editing
                      const { localDate, localTime } = utcToLocal(job.selectedDate, job.selectedTime);
                      const editJobWithLocalTime = {
                        ...job,
                        selectedDate: localDate || job.selectedDate,
                        selectedTime: localTime ? `${localTime}:00` : job.selectedTime
                      };
                      setEditedJob(editJobWithLocalTime);
                      setIsEditing(true);
                    }}
                  >
                    <Text style={styles.editButtonText}>Edit</Text>
                  </TouchableOpacity>
                )}
                {canDeleteThisJob() && (
                  <TouchableOpacity 
                    style={[styles.deleteButton, !canEditThisJob() && { flex: 1, marginRight: 0 }]} 
                    onPress={deleteJob}
                  >
                    <Text style={styles.deleteButtonText}>Delete</Text>
                  </TouchableOpacity>
                )}
              </>
            )}
          </View>
        )}

        {/* Convert / Create Quote / Revert */}
        {canEditThisJob() && job.visitType === 'quote' && (
          <>
            <TouchableOpacity
              style={styles.convertButton}
              onPress={async () => {
                if (!Number(job.price || 0)) {
                  const setPrice = window.confirm('Price is $0.00. Would you like to set a price first?');
                  if (setPrice) {
                    const p = window.prompt('Enter price for the job:', '');
                    if (p && !Number.isNaN(Number(p))) {
                      await saveEdits({ visitType: 'job', price: Number(p) });
                      await loadJob();
                      return;
                    }
                  }
                }
                await saveEdits({ visitType: 'job' });
                await loadJob();
              }}
            >
              <Text style={styles.convertButtonText}>Convert to Job</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.createQuoteButton}
              onPress={() => {
                const qs = new URLSearchParams({
                  id: String(job.id),
                  customerId: String(job.customerId ?? ''),
                  customerName: job.customerName ?? '',
                  title: job.title ?? '',
                  description: job.description ?? '',
                  price: String(job.price ?? ''),
                }).toString();
                navigate(`/create-quote?${qs}`);
              }}
            >
              <Text style={styles.createQuoteButtonText}>Create Quote</Text>
            </TouchableOpacity>
          </>
        )}

        {canEditThisJob() && job.visitType === 'job' && (
          <TouchableOpacity
            style={styles.revertButton}
            onPress={async () => {
              await saveEdits({ visitType: 'quote' });
              await loadJob();
            }}
          >
            <Text style={styles.revertButtonText}>Revert to Quote</Text>
          </TouchableOpacity>
        )}
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const htmlInputStyle = {
  padding: 10,
  border: '1px solid #ccc',
  borderRadius: 5,
  outline: 'none',
  fontSize: 14,
};

const styles = StyleSheet.create({
  container: {
    padding: 20,
    paddingBottom: 100,
    backgroundColor: '#f0f8ff',
    flex: 1,
  },
  header: { fontSize: 24, fontWeight: 'bold', color: '#333', marginBottom: 10, textAlign: 'center' },

  label: {
    fontWeight: 'bold',
    marginTop: 15,
    fontSize: 20,
    color: '#333',
    borderBottomWidth: 1,
    borderBottomColor: '#ddd',
    paddingBottom: 5,
  },
  value: { fontSize: 16, marginTop: 5, color: '#555' },

  input: {
    borderWidth: 1, borderColor: '#ccc', padding: 8, borderRadius: 4, marginTop: 5,
    backgroundColor: '#fff', shadowColor: '#000', shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1, shadowRadius: 2, elevation: 1,
  },

  buttonRow: { marginTop: 30, flexDirection: 'row', justifyContent: 'space-between' },
  editButton: {
    backgroundColor: '#007AFF', padding: 10, borderRadius: 5, alignItems: 'center',
    flex: 1, marginRight: 10, shadowColor: '#000', shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2, shadowRadius: 3, elevation: 2,
  },
  editButtonText: { color: '#fff', fontSize: 16, fontWeight: 'bold' },

  deleteButton: {
    backgroundColor: '#FF3B30', padding: 10, borderRadius: 5, alignItems: 'center',
    flex: 1, shadowColor: '#000', shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2, shadowRadius: 3, elevation: 2,
  },
  deleteButtonText: { color: '#fff', fontSize: 16, fontWeight: 'bold' },

  saveButton: {
    backgroundColor: '#34C759', padding: 10, borderRadius: 5, alignItems: 'center',
    flex: 1, shadowColor: '#000', shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2, shadowRadius: 3, elevation: 2,
  },
  saveButtonText: { color: '#fff', fontSize: 16, fontWeight: 'bold' },

  cancelButton: {
    backgroundColor: '#FF3B30', padding: 10, borderRadius: 8, alignItems: 'center',
    marginLeft: 10, flex: 1,
  },
  cancelButtonText: { color: '#fff', fontSize: 16, fontWeight: 'bold' },

  convertButton: {
    backgroundColor: '#FF9500', padding: 10, borderRadius: 5, alignItems: 'center',
    marginTop: 20, shadowColor: '#000', shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2, shadowRadius: 3, elevation: 2,
  },
  convertButtonText: { color: '#fff', fontSize: 16, fontWeight: 'bold' },

  revertButton: {
    backgroundColor: '#007AFF', padding: 10, borderRadius: 5, alignItems: 'center',
    marginTop: 20, shadowColor: '#000', shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2, shadowRadius: 3, elevation: 2,
  },
  revertButtonText: { color: '#fff', fontSize: 16, fontWeight: 'bold' },

  createQuoteButton: {
    backgroundColor: '#34C759', padding: 10, borderRadius: 5, alignItems: 'center',
    marginTop: 20, shadowColor: '#000', shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2, shadowRadius: 3, elevation: 2,
  },
  createQuoteButtonText: { color: '#fff', fontSize: 16, fontWeight: 'bold' },

  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  userPickerCard: {
    backgroundColor: '#fff',
    borderRadius: 10,
    width: '80%',
    maxHeight: '70%',
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 3,
    elevation: 5,
  },
  userPickerHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  closeButton: {
    padding: 5,
  },
  closeButtonText: {
    fontSize: 18,
    color: '#007AFF',
  },
  userListScroll: {
    marginBottom: 10,
  },
  userOption: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#ddd',
  },
  userOptionText: {
    fontSize: 16,
    color: '#333',
  },
  checkMark: {
    fontSize: 16,
    color: '#007AFF',
  },
  modalButton: {
    backgroundColor: '#007AFF',
    padding: 10,
    borderRadius: 5,
    alignItems: 'center',
  },
  modalButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  assignedUsersContainer: {
    borderWidth: 1,
    borderColor: '#ccc',
    padding: 8,
    borderRadius: 4,
    marginTop: 5,
    backgroundColor: '#fff',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 1,
  },
  assignedUsersText: {
    fontSize: 16,
    color: '#555',
  },
  nonEditableContainer: {
    backgroundColor: '#f9f9f9',
    borderColor: '#ddd',
  },
  nonEditableText: {
    color: '#aaa',
  },
  customerDetailsButton: {
    backgroundColor: '#007AFF',
    padding: 5,
    marginTop: 10,
    borderRadius: 5,
  },
  customerDetailsButtonText: {
    color: '#fff',
    fontSize: 14,
  },
  customerDetailsLink: {
    color: '#007AFF',
    textDecorationLine: 'underline',
    fontSize: 14,
  },
});
